import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import InvitationPreviewModal from '../../components/InvitationPreviewModal';
import { apiService } from '../../services/apiService';
import { getLoginRequiredNotification } from '../../utils/notificationHelpers';

const InviteGenerator = () => {
  const [searchParams] = useSearchParams();
  
  // State
  const [formData, setFormData] = useState({
    brideNames: '',
    groomNames: '',
    weddingDate: '',
    weddingTime: '',
    venue: '',
    address: '',
    rsvpDate: '',
    additionalInfo: ''
  });
  const [isVisible, setIsVisible] = useState(false);
  const [previewStyle, setPreviewStyle] = useState('watercolor-floral');
  const [generatedInvitation, setGeneratedInvitation] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiTextOptions, setAiTextOptions] = useState([]);
  const [selectedTextIndex, setSelectedTextIndex] = useState(null);
  const [customText, setCustomText] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [savedInvitationId, setSavedInvitationId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  const inviteStyles = [
    { 
      id: 'watercolor-floral', 
      name: 'Watercolor Floral', 
      gradient: 'from-pink-100 via-rose-50 to-purple-100',
      accent: '#ec4899',
      description: 'Soft painted flowers with romantic elegance',
      decorative: 'watercolor',
      background: 'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(167, 139, 250, 0.15) 0%, transparent 50%), radial-gradient(circle at 40% 20%, rgba(251, 207, 232, 0.2) 0%, transparent 40%)',
      font: 'font-serif'
    },
    { 
      id: 'gold-geometric', 
      name: 'Gold Geometric Luxury', 
      gradient: 'from-amber-50 via-yellow-50 to-orange-50',
      accent: '#d97706',
      description: 'Metallic frames with Art Deco elegance',
      decorative: 'geometric-luxury',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)',
      font: 'font-serif'
    },
    { 
      id: 'rustic-greenery', 
      name: 'Rustic Greenery', 
      gradient: 'from-emerald-50 via-green-50 to-teal-50',
      accent: '#059669',
      description: 'Botanical leaves with natural charm',
      decorative: 'botanical-luxury',
      background: 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(5, 150, 105, 0.12) 0%, transparent 50%)',
      font: 'font-serif'
    },
    { 
      id: 'marble-elegance', 
      name: 'Modern Marble', 
      gradient: 'from-gray-50 via-slate-50 to-stone-50',
      accent: '#475569',
      description: 'Elegant stone texture with sophistication',
      decorative: 'marble',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%, #f8fafc 100%)',
      font: 'font-sans'
    },
    { 
      id: 'vintage-lace', 
      name: 'Vintage Lace', 
      gradient: 'from-amber-50 via-orange-50 to-yellow-50',
      accent: '#ea580c',
      description: 'Delicate lace patterns with timeless beauty',
      decorative: 'lace',
      background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(251, 146, 60, 0.03) 10px, rgba(251, 146, 60, 0.03) 20px)',
      font: 'font-serif'
    },
    { 
      id: 'beach-watercolor', 
      name: 'Beach Watercolor', 
      gradient: 'from-cyan-50 via-blue-50 to-sky-50',
      accent: '#06b6d4',
      description: 'Ocean waves with coastal serenity',
      decorative: 'waves-luxury',
      background: 'radial-gradient(ellipse at 50% 80%, rgba(6, 182, 212, 0.15) 0%, transparent 50%), linear-gradient(180deg, #ecfeff 0%, #cffafe 100%)',
      font: 'font-sans'
    },
    { 
      id: 'rose-garden', 
      name: 'Romantic Rose Garden', 
      gradient: 'from-rose-100 via-pink-100 to-red-100',
      accent: '#f43f5e',
      description: 'Layered florals with dreamy romance',
      decorative: 'roses',
      background: 'radial-gradient(circle at 30% 30%, rgba(244, 63, 94, 0.12) 0%, transparent 40%), radial-gradient(circle at 70% 70%, rgba(251, 113, 133, 0.1) 0%, transparent 40%)',
      font: 'font-serif'
    },
    { 
      id: 'art-deco', 
      name: 'Art Deco Gold', 
      gradient: 'from-yellow-50 via-amber-50 to-orange-50',
      accent: '#f59e0b',
      description: '1920s glamour with golden accents',
      decorative: 'art-deco',
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 25%, #fde68a 50%, #fef3c7 75%, #fffbeb 100%)',
      font: 'font-serif'
    },
    { 
      id: 'minimalist-chic', 
      name: 'Minimalist Chic', 
      gradient: 'from-white via-gray-50 to-slate-50',
      accent: '#1f2937',
      description: 'Clean elegance with modern simplicity',
      decorative: 'minimal',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
      font: 'font-sans'
    },
    { 
      id: 'bohemian-wildflowers', 
      name: 'Bohemian Wildflowers', 
      gradient: 'from-purple-100 via-pink-100 to-orange-100',
      accent: '#a855f7',
      description: 'Free-spirited blooms with artistic flair',
      decorative: 'wildflowers',
      background: 'radial-gradient(circle at 20% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(251, 146, 60, 0.1) 0%, transparent 50%)',
      font: 'font-serif'
    }
  ];

  const textTones = [
    { id: 'formal', name: 'Classic Formal', icon: '🎩', description: 'Traditional and elegant' },
    { id: 'romantic', name: 'Romantic Poetry', icon: '💕', description: 'Poetic and dreamy' },
    { id: 'casual', name: 'Modern Casual', icon: '✨', description: 'Warm and friendly' },
    { id: 'heartfelt', name: 'Heartfelt Emotional', icon: '💖', description: 'Deep and touching' },
    { id: 'joyful', name: 'Joyful Celebration', icon: '🎉', description: 'Upbeat and festive' },
    { id: 'elegant', name: 'Timeless Elegance', icon: '👑', description: 'Sophisticated and refined' },
    { id: 'whimsical', name: 'Whimsical Fun', icon: '🦋', description: 'Playful and lighthearted' },
    { id: 'spiritual', name: 'Spiritual Blessing', icon: '🙏', description: 'Meaningful and blessed' }
  ];

  useEffect(() => {
    setIsVisible(true);
    loadUserData();
  }, []);

  // Load existing invitation if UUID is in URL
  useEffect(() => {
    const invitationUuid = searchParams.get('uuid');
    if (invitationUuid) {
      loadInvitation(invitationUuid);
    }
  }, [searchParams]);

  // Load existing invitation for editing
  const loadInvitation = async (uuid) => {
    try {
      const response = await apiService.invitations.get(uuid);
      const inv = response.data.invitation;
      
      // Helper to convert ISO date to YYYY-MM-DD
      const formatDateForInput = (isoDate) => {
        if (!isoDate) return '';
        return isoDate.split('T')[0];
      };
      
      setFormData({
        brideNames: inv.bride_names || '',
        groomNames: inv.groom_names || '',
        weddingDate: formatDateForInput(inv.wedding_date),
        weddingTime: inv.wedding_time || '',
        venue: inv.venue || '',
        address: inv.address || '',
        rsvpDate: formatDateForInput(inv.rsvp_date),
        additionalInfo: inv.additional_info || ''
      });
      
      if (inv.template_id) setPreviewStyle(inv.template_id);
      if (inv.invitation_text) setGeneratedInvitation({ text: inv.invitation_text });
      setSavedInvitationId(inv.uuid);
    } catch (error) {
      console.error('Error loading invitation:', error);
      alert('Failed to load invitation');
    }
  };

  // Save invitation
  const saveInvitation = async () => {
    console.log('💾 SAVE INVITATION - START');
    
    // Validate form first
    if (!validateForm()) {
      setNotification({
        show: true,
        title: 'Validation Error',
        message: 'Please fix the errors in the form before saving.',
        type: 'warning'
      });
      return;
    }
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('❌ No token found');
      setNotification(getLoginRequiredNotification());
      return;
    }
    
    // Validate required fields
    if (!formData.brideNames || !formData.groomNames) {
      console.log('❌ Missing required fields');
      setNotification({
        show: true,
        title: 'Missing Information',
        message: 'Please enter both bride and groom names before saving your invitation.',
        type: 'warning'
      });
      return;
    }
    
    console.log('✅ Validation passed, preparing to save...');
    setIsSaving(true);
    try {
      const invitationData = {
        bride_names: formData.brideNames,
        groom_names: formData.groomNames,
        wedding_date: formData.weddingDate,
        wedding_time: formData.weddingTime,
        venue: formData.venue,
        address: formData.address,
        rsvp_date: formData.rsvpDate,
        additional_info: formData.additionalInfo,
        template_id: previewStyle,
        tone: selectedTextIndex !== null && selectedTextIndex !== 'custom' 
          ? aiTextOptions[selectedTextIndex]?.tone.id 
          : 'custom',
        invitation_text: generatedInvitation?.text || customText
      };

      console.log('📤 Sending invitation data:', invitationData);

      if (savedInvitationId) {
        console.log('🔄 Updating existing invitation:', savedInvitationId);
        // Update existing
        await apiService.invitations.update(savedInvitationId, invitationData);
        console.log('✅ Update successful');
        setNotification({
          show: true,
          title: 'Invitation Updated!',
          message: '💌 Your wedding invitation has been updated successfully! Visit your Invitations page to view it.',
          type: 'success'
        });
      } else {
        console.log('➕ Creating new invitation');
        // Create new
        const response = await apiService.invitations.save(invitationData);
        console.log('✅ Save successful, response:', response.data);
        setSavedInvitationId(response.data.invitation.uuid);
        setNotification({
          show: true,
          title: 'Invitation Saved!',
          message: '💌 Your wedding invitation has been saved successfully! Visit your Invitations page to view and manage all your saved invitations.',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('❌ Error saving invitation:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      setNotification({
        show: true,
        title: 'Save Failed',
        message: error.response?.data?.error || 'Failed to save invitation. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
      console.log('💾 SAVE INVITATION - END');
    }
  };

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await apiService.users.getProfile();
        if (response.data.wedding_date) {
          setFormData(prev => ({ ...prev, weddingDate: response.data.wedding_date }));
        }
        if (response.data.partner_name) {
          setFormData(prev => ({ ...prev, groomNames: response.data.partner_name }));
        }
        if (response.data.venue_location) {
          setFormData(prev => ({ ...prev, venue: response.data.venue_location }));
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Validate on change
    if (field === 'weddingDate') {
      // Clear RSVP date if it's after new wedding date
      if (formData.rsvpDate && value && new Date(formData.rsvpDate) >= new Date(value)) {
        setFormData(prev => ({ ...prev, rsvpDate: '' }));
        setValidationErrors(prev => ({ ...prev, rsvpDate: 'RSVP date cleared - must be before wedding date' }));
      }
    }
    
    if (field === 'rsvpDate' && value) {
      // Validate RSVP date is before wedding date
      if (formData.weddingDate && new Date(value) >= new Date(formData.weddingDate)) {
        setValidationErrors(prev => ({ ...prev, rsvpDate: 'RSVP date must be before wedding date' }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.brideNames?.trim()) {
      errors.brideNames = 'Bride name is required';
    }
    
    if (!formData.groomNames?.trim()) {
      errors.groomNames = 'Groom name is required';
    }
    
    if (formData.weddingDate) {
      const weddingDate = new Date(formData.weddingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (weddingDate < today) {
        errors.weddingDate = 'Wedding date cannot be in the past';
      }
    }
    
    if (formData.rsvpDate) {
      if (!formData.weddingDate) {
        errors.rsvpDate = 'Please set wedding date first';
      } else {
        const rsvpDate = new Date(formData.rsvpDate);
        const weddingDate = new Date(formData.weddingDate);
        
        if (rsvpDate >= weddingDate) {
          errors.rsvpDate = 'RSVP date must be before wedding date';
        }
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (rsvpDate < today) {
          errors.rsvpDate = 'RSVP date cannot be in the past';
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearForm = () => {
    setFormData({
      brideNames: '',
      groomNames: '',
      weddingDate: '',
      weddingTime: '',
      venue: '',
      address: '',
      rsvpDate: '',
      additionalInfo: ''
    });
    setAiTextOptions([]);
    setSelectedTextIndex(null);
    setGeneratedInvitation(null);
    setValidationErrors({});
  };

  const generateAIInvitation = async () => {
    if (!validateForm()) {
      setNotification({
        show: true,
        title: 'Validation Error',
        message: 'Please fix the errors in the form before generating invitation text.',
        type: 'warning'
      });
      return;
    }

    if (!formData.brideNames || !formData.groomNames || !formData.weddingDate || !formData.venue) {
      return;
    }

    setIsGenerating(true);
    setAiTextOptions([]);
    setSelectedTextIndex(null);
    
    try {
      const promises = textTones.map(tone => 
        apiService.invitations.generate({
          bride_name: formData.brideNames,
          groom_name: formData.groomNames,
          wedding_date: formData.weddingDate,
          time: formData.weddingTime,
          venue: formData.venue,
          address: formData.address,
          theme: previewStyle,
          tone: tone.id,
          rsvp_details: formData.rsvpDate ? `Please RSVP by ${formData.rsvpDate}` : null,
          special_requests: formData.additionalInfo
        })
      );

      const results = await Promise.all(promises);
      
      const options = results.map((response, index) => ({
        text: response.data.invitation.text,
        tone: textTones[index],
        id: index
      }));

      setAiTextOptions(options);
      setSelectedTextIndex(0);
      setGeneratedInvitation({ text: options[0].text });
      
    } catch (error) {
      console.error('Error generating invitation:', error);
      alert('Failed to generate invitation options. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const selectTextOption = (index) => {
    setSelectedTextIndex(index);
    if (index === null) {
      setGeneratedInvitation(null);
    } else if (index === 'custom') {
      setGeneratedInvitation({ text: customText });
    } else {
      setGeneratedInvitation({ text: aiTextOptions[index].text });
    }
  };

  const renderDecorativeElements = (decorativeType, accentColor) => {
    const currentStyle = inviteStyles.find(s => s.id === previewStyle);
    if (!currentStyle) return null;

    switch (decorativeType) {
      case 'watercolor':
        return (
          <>
            <div className="absolute top-0 left-0 w-32 h-32 opacity-40">
              <div className="text-6xl">🌸</div>
              <div className="absolute top-8 left-8 text-4xl opacity-70">🌺</div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 opacity-40 transform scale-x-[-1]">
              <div className="text-6xl">🌸</div>
              <div className="absolute top-8 right-8 text-4xl opacity-70">🌺</div>
            </div>
            <div className="absolute bottom-0 left-0 text-7xl opacity-30">🌷</div>
            <div className="absolute bottom-0 right-0 text-7xl opacity-30 transform scale-x-[-1]">🌷</div>
          </>
        );
      
      case 'geometric-luxury':
        return (
          <>
            <div className="absolute top-4 left-4 w-20 h-20 border-t-4 border-l-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute top-4 right-4 w-20 h-20 border-t-4 border-r-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-4 left-4 w-20 h-20 border-b-4 border-l-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-4 right-4 w-20 h-20 border-b-4 border-r-4 opacity-50" style={{ borderColor: accentColor }} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 border-2 rotate-45 opacity-15" style={{ borderColor: accentColor }} />
          </>
        );
      
      case 'botanical-luxury':
        return (
          <>
            <div className="absolute top-0 left-0 text-7xl opacity-35">🌿</div>
            <div className="absolute top-0 right-0 text-7xl opacity-35 transform scale-x-[-1]">🌿</div>
            <div className="absolute bottom-0 left-0 text-7xl opacity-35 transform rotate-180">🌿</div>
            <div className="absolute bottom-0 right-0 text-7xl opacity-35 transform scale-x-[-1] rotate-180">🌿</div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">🍃</div>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">🍃</div>
          </>
        );
      
      case 'marble':
        return (
          <>
            <div className="absolute top-0 left-0 right-0 h-px opacity-20" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute bottom-0 left-0 right-0 h-px opacity-20" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute top-2 left-2 w-12 h-12 border-t-2 border-l-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute top-2 right-2 w-12 h-12 border-t-2 border-r-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-2 left-2 w-12 h-12 border-b-2 border-l-2 opacity-30" style={{ borderColor: accentColor }} />
            <div className="absolute bottom-2 right-2 w-12 h-12 border-b-2 border-r-2 opacity-30" style={{ borderColor: accentColor }} />
          </>
        );
      
      case 'lace':
        return (
          <>
            <div className="absolute top-3 left-3 text-5xl opacity-35">❦</div>
            <div className="absolute top-3 right-3 text-5xl opacity-35 transform scale-x-[-1]">❦</div>
            <div className="absolute bottom-3 left-3 text-5xl opacity-35 transform rotate-180">❦</div>
            <div className="absolute bottom-3 right-3 text-5xl opacity-35 transform scale-x-[-1] rotate-180">❦</div>
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-4xl opacity-30">✿</div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-4xl opacity-30">✿</div>
          </>
        );
      
      case 'waves-luxury':
        return (
          <>
            <div className="absolute top-6 left-6 text-4xl opacity-35">🐚</div>
            <div className="absolute top-6 right-6 text-4xl opacity-35">🐚</div>
            <div className="absolute bottom-6 left-6 text-4xl opacity-35">⭐</div>
            <div className="absolute bottom-6 right-6 text-4xl opacity-35">⭐</div>
          </>
        );
      
      case 'roses':
        return (
          <>
            <div className="absolute top-0 left-0 text-6xl opacity-35">🌹</div>
            <div className="absolute top-0 right-0 text-6xl opacity-35">🌹</div>
            <div className="absolute bottom-0 left-0 text-6xl opacity-35">🌹</div>
            <div className="absolute bottom-0 right-0 text-6xl opacity-35">🌹</div>
            <div className="absolute top-12 left-12 text-3xl opacity-25">🥀</div>
            <div className="absolute top-12 right-12 text-3xl opacity-25">🥀</div>
          </>
        );
      
      case 'art-deco':
        return (
          <>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">◆</div>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-5xl opacity-40">◆</div>
            <div className="absolute top-6 left-6 text-4xl opacity-35">✦</div>
            <div className="absolute top-6 right-6 text-4xl opacity-35">✦</div>
            <div className="absolute bottom-6 left-6 text-4xl opacity-35">✦</div>
            <div className="absolute bottom-6 right-6 text-4xl opacity-35">✦</div>
          </>
        );
      
      case 'minimal':
        return (
          <>
            <div className="absolute top-0 left-0 right-0 h-px opacity-25" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <div className="absolute bottom-0 left-0 right-0 h-px opacity-25" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
          </>
        );
      
      case 'wildflowers':
        return (
          <>
            <div className="absolute top-2 left-4 text-5xl opacity-35">🌼</div>
            <div className="absolute top-8 right-8 text-4xl opacity-30">🌻</div>
            <div className="absolute bottom-4 right-4 text-5xl opacity-35">🌺</div>
            <div className="absolute bottom-12 left-8 text-4xl opacity-30">🌷</div>
          </>
        );
      
      default:
        return null;
    }
  };

  const getDecorativeHTML = (type, color) => {
    switch(type) {
      case 'watercolor':
        return `<div style="position:absolute;top:0;left:0;font-size:80px;opacity:0.3">🌸</div><div style="position:absolute;top:0;right:0;font-size:80px;opacity:0.3">🌺</div><div style="position:absolute;bottom:0;left:0;font-size:80px;opacity:0.3">🌷</div><div style="position:absolute;bottom:0;right:0;font-size:80px;opacity:0.3">🌹</div>`;
      case 'geometric-luxury':
        return `<div style="position:absolute;top:16px;left:16px;width:80px;height:80px;border-top:4px solid ${color};border-left:4px solid ${color};opacity:0.5"></div><div style="position:absolute;top:16px;right:16px;width:80px;height:80px;border-top:4px solid ${color};border-right:4px solid ${color};opacity:0.5"></div><div style="position:absolute;bottom:16px;left:16px;width:80px;height:80px;border-bottom:4px solid ${color};border-left:4px solid ${color};opacity:0.5"></div><div style="position:absolute;bottom:16px;right:16px;width:80px;height:80px;border-bottom:4px solid ${color};border-right:4px solid ${color};opacity:0.5"></div>`;
      case 'botanical-luxury':
        return `<div style="position:absolute;top:0;left:0;font-size:70px;opacity:0.35">🌿</div><div style="position:absolute;top:0;right:0;font-size:70px;opacity:0.35;transform:scaleX(-1)">🌿</div><div style="position:absolute;bottom:0;left:0;font-size:70px;opacity:0.35">🌿</div><div style="position:absolute;bottom:0;right:0;font-size:70px;opacity:0.35">🌿</div>`;
      case 'marble':
        return `<div style="position:absolute;top:8px;left:8px;width:48px;height:48px;border-top:2px solid ${color};border-left:2px solid ${color};opacity:0.3"></div><div style="position:absolute;top:8px;right:8px;width:48px;height:48px;border-top:2px solid ${color};border-right:2px solid ${color};opacity:0.3"></div>`;
      case 'lace':
        return `<div style="position:absolute;top:12px;left:12px;font-size:50px;opacity:0.35">❦</div><div style="position:absolute;top:12px;right:12px;font-size:50px;opacity:0.35">❦</div><div style="position:absolute;bottom:12px;left:12px;font-size:50px;opacity:0.35">❦</div><div style="position:absolute;bottom:12px;right:12px;font-size:50px;opacity:0.35">❦</div>`;
      case 'waves-luxury':
        return `<div style="position:absolute;top:24px;left:24px;font-size:40px;opacity:0.35">🐚</div><div style="position:absolute;top:24px;right:24px;font-size:40px;opacity:0.35">🐚</div><div style="position:absolute;bottom:24px;left:24px;font-size:40px;opacity:0.35">⭐</div><div style="position:absolute;bottom:24px;right:24px;font-size:40px;opacity:0.35">⭐</div>`;
      case 'roses':
        return `<div style="position:absolute;top:0;left:0;font-size:60px;opacity:0.35">🌹</div><div style="position:absolute;top:0;right:0;font-size:60px;opacity:0.35">🌹</div><div style="position:absolute;bottom:0;left:0;font-size:60px;opacity:0.35">🌹</div><div style="position:absolute;bottom:0;right:0;font-size:60px;opacity:0.35">🌹</div>`;
      case 'art-deco':
        return `<div style="position:absolute;top:16px;left:50%;transform:translateX(-50%);font-size:50px;opacity:0.4">◆</div><div style="position:absolute;bottom:16px;left:50%;transform:translateX(-50%);font-size:50px;opacity:0.4">◆</div><div style="position:absolute;top:24px;left:24px;font-size:40px;opacity:0.35">✦</div><div style="position:absolute;top:24px;right:24px;font-size:40px;opacity:0.35">✦</div>`;
      case 'minimal':
        return `<div style="position:absolute;top:0;left:0;right:0;height:1px;opacity:0.25;background:linear-gradient(90deg, transparent, ${color}, transparent)"></div><div style="position:absolute;bottom:0;left:0;right:0;height:1px;opacity:0.25;background:linear-gradient(90deg, transparent, ${color}, transparent)"></div>`;
      case 'wildflowers':
        return `<div style="position:absolute;top:8px;left:16px;font-size:50px;opacity:0.35">🌼</div><div style="position:absolute;top:32px;right:32px;font-size:40px;opacity:0.3">🌻</div><div style="position:absolute;bottom:16px;right:16px;font-size:50px;opacity:0.35">🌺</div><div style="position:absolute;bottom:48px;left:32px;font-size:40px;opacity:0.3">🌷</div>`;
      default:
        return '';
    }
  };

  const downloadInvite = async () => {
    if (!formData.brideNames || !formData.groomNames) {
      alert('Please fill in bride and groom names');
      return;
    }

    setIsDownloading(true);
    try {
      let invitationText = generatedInvitation?.text || '';
      const currentTemplate = inviteStyles.find(s => s.id === previewStyle);

      // Format dates for print
      const formatDateForPrint = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      };

      const formattedWeddingDate = formatDateForPrint(formData.weddingDate) || 'Wedding Date';
      const formattedRsvpDate = formData.rsvpDate ? formatDateForPrint(formData.rsvpDate) : '';

      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Wedding Invitation - ${formData.brideNames} & ${formData.groomNames}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Montserrat:wght@400;600&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: ${currentTemplate.font === 'font-serif' ? "'Playfair Display', serif" : "'Montserrat', sans-serif"};
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: 20px;
            }
            .invitation {
              width: 600px;
              max-width: 100%;
              padding: 60px 40px;
              text-align: center;
              border: 4px solid ${currentTemplate.accent};
              border-radius: 16px;
              background: ${currentTemplate.background};
              position: relative;
              overflow: hidden;
              box-shadow: 0 20px 60px rgba(0,0,0,0.15);
            }
            .names { font-size: 36px; font-weight: bold; color: ${currentTemplate.accent}; margin-bottom: 30px; position: relative; z-index: 10; }
            .invitation-text { font-size: 18px; line-height: 1.8; color: #374151; margin: 30px 0; position: relative; z-index: 10; }
            .divider { width: 80px; height: 2px; background: ${currentTemplate.accent}; margin: 20px auto; position: relative; z-index: 10; }
            .details { font-size: 20px; font-weight: bold; color: ${currentTemplate.accent}; margin: 20px 0; position: relative; z-index: 10; }
            .info { font-size: 16px; color: #4b5563; margin: 10px 0; position: relative; z-index: 10; }
            @media print { 
              body { padding: 0; } 
              .invitation { 
                box-shadow: none;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="invitation">
            ${getDecorativeHTML(currentTemplate.decorative, currentTemplate.accent)}
            <div class="names">${formData.brideNames} & ${formData.groomNames}</div>
            ${invitationText ? `<div class="invitation-text">${invitationText}</div>` : ''}
            <div class="divider"></div>
            <div class="details">${formattedWeddingDate}</div>
            ${formData.weddingTime ? `<div class="info">${formData.weddingTime}</div>` : ''}
            <div class="divider"></div>
            <div class="details">${formData.venue || 'Wedding Venue'}</div>
            ${formData.address ? `<div class="info">${formData.address}</div>` : ''}
            ${formattedRsvpDate ? `<div class="divider"></div><div class="info">RSVP by ${formattedRsvpDate}</div>` : ''}
            ${formData.additionalInfo ? `<div class="divider"></div><div class="info">${formData.additionalInfo}</div>` : ''}
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printHTML);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
      
    } catch (error) {
      console.error('Error downloading invitation:', error);
      alert('Failed to generate invitation. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const pageIcons = [
    { emoji: '💌', size: '6xl', opacity: '15' },
    { emoji: '💕', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '3xl', opacity: '15' },
    { emoji: '📜', size: '8xl', opacity: '15' },
    { emoji: '🎨', size: '6xl', opacity: '15' },
    { emoji: '✨', size: '5xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Floating sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">💌</span>
              <span className="absolute -top-1 -right-1 text-lg animate-pulse">💕</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Invite Generator
          </h1>
          
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Create beautiful, personalized wedding invitations that capture your unique love story
          </p>
        </div>

        {/* 2x2 Grid Layout */}
        <div className="grid lg:grid-cols-2 gap-8" style={{ gridTemplateRows: 'repeat(2, 600px)' }}>
          
          {/* [1,1] Invitation Details */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8 h-full flex flex-col">
              <div className="flex items-center mb-8 flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                  <span className="text-2xl">📝</span>
                </div>
                <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                  Invitation Details
                </h3>
              </div>
              
              <div className="space-y-6 overflow-y-auto flex-1 pr-2">
                {/* Couple Names */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-rose-400">
                      <span className="text-xl">👰</span>
                    </div>
                    <input
                      type="text"
                      value={formData.brideNames}
                      onChange={(e) => handleInputChange('brideNames', e.target.value)}
                      placeholder="Bride's Name(s)"
                      className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:ring-4 focus:ring-gold-200 transition-all duration-300 bg-white/80 font-medium ${
                        validationErrors.brideNames ? 'border-red-400 focus:border-red-400' : 'border-champagne-200 focus:border-gold-400'
                      }`}
                    />
                    {validationErrors.brideNames && (
                      <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.brideNames}</p>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">
                      <span className="text-xl">🤵</span>
                    </div>
                    <input
                      type="text"
                      value={formData.groomNames}
                      onChange={(e) => handleInputChange('groomNames', e.target.value)}
                      placeholder="Groom's Name(s)"
                      className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:ring-4 focus:ring-gold-200 transition-all duration-300 bg-white/80 font-medium ${
                        validationErrors.groomNames ? 'border-red-400 focus:border-red-400' : 'border-champagne-200 focus:border-gold-400'
                      }`}
                    />
                    {validationErrors.groomNames && (
                      <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.groomNames}</p>
                    )}
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400">
                      <span className="text-xl">📅</span>
                    </div>
                    <input
                      type="date"
                      value={formData.weddingDate}
                      onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                      min={today}
                      className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:ring-4 focus:ring-gold-200 transition-all duration-300 bg-white/80 font-medium ${
                        validationErrors.weddingDate ? 'border-red-400 focus:border-red-400' : 'border-champagne-200 focus:border-gold-400'
                      }`}
                    />
                    {validationErrors.weddingDate && (
                      <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.weddingDate}</p>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-green-400">
                      <span className="text-xl">🕐</span>
                    </div>
                    <input
                      type="time"
                      value={formData.weddingTime}
                      onChange={(e) => handleInputChange('weddingTime', e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                  </div>
                </div>

                {/* Venue */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gold-400">
                    <span className="text-xl">🏛️</span>
                  </div>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) => handleInputChange('venue', e.target.value)}
                    placeholder="Wedding Venue"
                    className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                  />
                </div>

                {/* Address */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-teal-400">
                    <span className="text-xl">📍</span>
                  </div>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Venue Address"
                    className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                  />
                </div>

                {/* RSVP Date */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-pink-400">
                    <span className="text-xl">📮</span>
                  </div>
                  <input
                    type="date"
                    value={formData.rsvpDate}
                    onChange={(e) => handleInputChange('rsvpDate', e.target.value)}
                    min={today}
                    max={formData.weddingDate || undefined}
                    className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:ring-4 focus:ring-gold-200 transition-all duration-300 bg-white/80 font-medium ${
                      validationErrors.rsvpDate ? 'border-red-400 focus:border-red-400' : 'border-champagne-200 focus:border-gold-400'
                    }`}
                  />
                  <label className="absolute -top-2 left-4 bg-white px-2 text-sm font-medium text-gray-600">
                    RSVP By Date
                  </label>
                  {validationErrors.rsvpDate && (
                    <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.rsvpDate}</p>
                  )}
                </div>

                {/* Additional Info */}
                <div className="relative">
                  <div className="absolute left-4 top-4 text-indigo-400">
                    <span className="text-xl">💬</span>
                  </div>
                  <textarea
                    value={formData.additionalInfo}
                    onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                    placeholder="Additional Information (dress code, directions, special notes...)"
                    rows="4"
                    className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium resize-none"
                  />
                </div>

                {/* Clear Button */}
                <button
                  onClick={clearForm}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all duration-300"
                >
                  🔄 Clear Form
                </button>
              </div>
            </div>
          </div>

          {/* [1,2] AI-Generated Invitation Text */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-purple-200/50 p-8 h-full flex flex-col">
              <div className="flex items-center justify-between mb-8 flex-shrink-0">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl">✨</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      AI-Generated Invitation Text
                    </h3>
                    <p className="text-sm text-gray-600">Choose your perfect message</p>
                  </div>
                </div>
                {aiTextOptions.length > 0 && !isGenerating && (
                  <button
                    onClick={generateAIInvitation}
                    disabled={!formData.brideNames || !formData.groomNames || !formData.weddingDate || !formData.venue}
                    className="relative px-3 py-2 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 text-white rounded-xl font-semibold text-xs hover:from-rose-500 hover:via-pink-500 hover:to-gold-500 hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-1.5 shadow-lg overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-rose-300 via-pink-300 to-gold-300 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300"></div>
                    <span className="text-sm relative z-10">🔄</span>
                    <span className="relative z-10">Regenerate</span>
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto pr-2">
              {/* Generate Button */}
              {aiTextOptions.length === 0 && !isGenerating && (
                <div className="text-center py-16">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 w-20 h-20 rounded-full flex items-center justify-center shadow-2xl">
                      <span className="text-4xl">✨</span>
                    </div>
                  </div>
                  
                  <h4 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
                    Ready to Create Magic?
                  </h4>
                  
                  <p className="text-gray-600 mb-6 max-w-sm mx-auto leading-relaxed">
                    Fill in the wedding details to generate 8 magical invitation text options
                  </p>

                  <button
                    onClick={generateAIInvitation}
                    disabled={!formData.brideNames || !formData.groomNames || !formData.weddingDate || !formData.venue}
                    className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    ✨ Generate AI Text Options
                  </button>
                </div>
              )}

              {/* Loading State */}
              {isGenerating && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin text-5xl mb-4">⏳</div>
                  <div className="text-lg font-semibold text-gray-700 mb-2">Creating Magic...</div>
                  <div className="text-sm text-gray-500">Generating 8 beautiful invitation text options</div>
                </div>
              )}

              {/* AI Text Options */}
              {aiTextOptions.length > 0 && (
                <div className="grid grid-cols-1 gap-4">
                  {aiTextOptions.map((option, index) => (
                    <button
                      key={option.id}
                      onClick={() => selectTextOption(index)}
                      className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
                        selectedTextIndex === index
                          ? 'border-purple-400 bg-gradient-to-br from-purple-100 via-pink-50 to-rose-100 shadow-2xl scale-105 ring-4 ring-purple-200/50'
                          : 'border-purple-200/50 bg-white/80 backdrop-blur-sm hover:border-purple-300 hover:shadow-xl hover:scale-102'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <span className="text-2xl mr-2">{option.tone.icon}</span>
                        <div>
                          <div className="font-bold text-gray-800">{option.tone.name}</div>
                          <div className="text-xs text-gray-500">{option.tone.description}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                        {option.text}
                      </div>
                      {selectedTextIndex === index && (
                        <>
                          <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                            <span className="text-white text-lg font-bold">✓</span>
                          </div>
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-400/10 to-pink-400/10 pointer-events-none"></div>
                        </>
                      )}
                    </button>
                  ))}
                  
                  {/* Custom Option */}
                  <div
                    className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 ${
                      selectedTextIndex === 'custom'
                        ? 'border-indigo-400 bg-gradient-to-br from-indigo-100 via-blue-50 to-indigo-100 shadow-2xl scale-105 ring-4 ring-indigo-200/50'
                        : 'border-indigo-200/50 bg-white/80 backdrop-blur-sm'
                    }`}
                  >
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">✍️</span>
                      <div>
                        <div className="font-bold text-gray-800">Custom</div>
                        <div className="text-xs text-gray-500">Write your own invitation text</div>
                      </div>
                    </div>
                    <textarea
                      value={customText}
                      onChange={(e) => {
                        setCustomText(e.target.value);
                        setSelectedTextIndex('custom');
                        setGeneratedInvitation({ text: e.target.value });
                      }}
                      placeholder="Write your custom invitation text here..."
                      rows="4"
                      className="w-full p-3 border-2 border-indigo-200 rounded-xl focus:ring-4 focus:ring-indigo-200 focus:border-indigo-400 transition-all duration-300 bg-white/90 text-sm resize-none"
                    />
                    {selectedTextIndex === 'custom' && (
                      <>
                        <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                          <span className="text-white text-lg font-bold">✓</span>
                        </div>
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-400/10 to-blue-400/10 pointer-events-none"></div>
                      </>
                    )}
                  </div>
                  
                  {/* None Option */}
                  <button
                    onClick={() => selectTextOption(null)}
                    className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
                      selectedTextIndex === null
                        ? 'border-gray-400 bg-gradient-to-br from-gray-100 via-slate-50 to-gray-100 shadow-2xl scale-105 ring-4 ring-gray-200/50'
                        : 'border-gray-200/50 bg-white/80 backdrop-blur-sm hover:border-gray-300 hover:shadow-xl hover:scale-102'
                    }`}
                  >
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🚫</span>
                      <div>
                        <div className="font-bold text-gray-800">None</div>
                        <div className="text-xs text-gray-500">No invitation text</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 italic">
                      Blank - no invitation text will be shown
                    </div>
                    {selectedTextIndex === null && (
                      <>
                        <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-r from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                          <span className="text-white text-lg font-bold">✓</span>
                        </div>
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-400/10 to-slate-400/10 pointer-events-none"></div>
                      </>
                    )}
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>

          {/* [2,1] Professional Templates */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8 h-full flex flex-col">
              <div className="flex items-center mb-6 flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-gold-400 to-yellow-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                  <span className="text-2xl">🎨</span>
                </div>
                <div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent">
                    Professional Templates
                  </h3>
                  <p className="text-sm text-gray-600">Choose your invitation style</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 overflow-y-auto flex-1 pr-2">
                {inviteStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setPreviewStyle(style.id)}
                    style={{ background: style.background }}
                    className={`group relative p-5 rounded-2xl border-2 transition-all duration-300 text-left overflow-hidden backdrop-blur-sm ${
                      previewStyle === style.id
                        ? `border-rose-400 shadow-2xl scale-105 ring-4 ring-rose-200/50`
                        : `border-champagne-200 hover:border-rose-300 hover:shadow-xl hover:scale-102`
                    }`}
                  >
                    <div className={`text-lg font-bold mb-2 font-serif transition-all duration-300 ${
                      previewStyle === style.id 
                        ? 'bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent'
                        : 'text-gray-800 group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:via-pink-600 group-hover:to-gold-600 group-hover:bg-clip-text group-hover:text-transparent'
                    }`}>
                      {style.name}
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      {style.description}
                    </div>
                    {previewStyle === style.id && (
                      <>
                        <div className="absolute -top-2 -right-2 w-9 h-9 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                          <span className="text-white text-sm font-bold">✓</span>
                        </div>
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-rose-400/10 via-pink-400/10 to-gold-400/10 pointer-events-none"></div>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* [2,2] Live Preview */}
          <div className={`transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-rose-200/50 p-6 lg:p-8 h-full flex flex-col">
              <div className="flex items-center mb-4 lg:mb-6 flex-shrink-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-3 lg:mr-4 shadow-lg animate-pulse">
                  <span className="text-xl lg:text-2xl">👁️</span>
                </div>
                <div>
                  <h3 className="text-xl lg:text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Live Preview
                  </h3>
                  <p className="text-xs lg:text-sm text-gray-600">See your invitation in real-time</p>
                </div>
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-y-auto pr-2">
                {previewStyle ? (
                  <InvitationPreviewModal
                    isOpen={true}
                    invitation={{
                      brideNames: formData.brideNames,
                      groomNames: formData.groomNames,
                      text: generatedInvitation?.text,
                      weddingDate: formData.weddingDate,
                      weddingTime: formData.weddingTime,
                      venue: formData.venue,
                      address: formData.address,
                      rsvpDate: formData.rsvpDate,
                      additionalInfo: formData.additionalInfo
                    }}
                    templateId={previewStyle}
                    inviteStyles={inviteStyles}
                    renderDecorativeElements={renderDecorativeElements}
                    variant="inline"
                  />
                ) : (
                  <div className="text-center py-12 lg:py-16">
                    <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-6 shadow-lg">
                      <span className="text-4xl lg:text-5xl">🎨</span>
                    </div>
                    <h4 className="text-lg lg:text-xl font-serif font-bold text-gray-700 mb-2 lg:mb-3">
                      Select a Template
                    </h4>
                    <p className="text-sm lg:text-base text-gray-600 max-w-xs mx-auto leading-relaxed">
                      Choose a professional template to see your invitation come to life
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Action Buttons */}
        <div className={`mt-12 transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex justify-center gap-6 flex-wrap">
            <button
              onClick={() => setShowPreviewModal(true)}
              disabled={!formData.brideNames || !formData.groomNames}
              className="px-8 py-4 bg-gradient-to-r from-gold-400 to-yellow-400 text-white rounded-2xl font-bold text-lg hover:from-gold-500 hover:to-yellow-500 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            >
              <span className="text-2xl">👁️</span>
              Preview Invitation
            </button>
            
            <button
              onClick={saveInvitation}
              disabled={isSaving || !formData.brideNames || !formData.groomNames}
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl font-bold text-lg hover:from-emerald-600 hover:to-teal-600 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            >
              <span className="text-2xl">💾</span>
              {isSaving ? 'Saving...' : savedInvitationId ? 'Update Invitation' : 'Save Invitation'}
            </button>
            
            <button
              onClick={downloadInvite}
              disabled={isDownloading || !formData.brideNames || !formData.groomNames}
              className="px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-2xl font-bold text-lg hover:from-rose-600 hover:to-pink-600 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            >
              <span className="text-2xl">🖨️</span>
              {isDownloading ? 'Preparing...' : 'Print Invitation'}
            </button>
          </div>
        </div>

        {/* Luxury Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Part of your personalized VowAI Wedding Journey
          </p>
        </div>

      </div>
      
      {/* Preview Modal */}
      <InvitationPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        invitation={{
          brideNames: formData.brideNames,
          groomNames: formData.groomNames,
          text: generatedInvitation?.text,
          weddingDate: formData.weddingDate,
          weddingTime: formData.weddingTime,
          venue: formData.venue,
          address: formData.address,
          rsvpDate: formData.rsvpDate,
          additionalInfo: formData.additionalInfo
        }}
        templateId={previewStyle}
        inviteStyles={inviteStyles}
        renderDecorativeElements={renderDecorativeElements}
      />
      
      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default InviteGenerator;
